/**
 * Konten auf dem eigenen PocketBase, ohne dessen Bibliothek: es sind ein paar
 * Abrufe, und die Daten-Schicht darüber ist ohnehin unsere eigene.
 *
 * Der Grundsatz: **erst lokal, dann zum Server.** Eine Tour entsteht auf dem Berg,
 * und dort ist das Netz das Letzte, worauf man sich verlässt. Jede Änderung landet
 * sofort im Gerät und geht dann hinaus; scheitert das am Netz, bleibt sie vorgemerkt
 * und wird nachgeholt. Abgelehnt wird nur, was der Server wirklich ablehnt.
 *
 * Die Kennungen entstehen im Gerät. Ein zweiter Versuch nach einem Abbruch legt
 * deshalb nichts doppelt an: die Kennung gibt es dann schon.
 */

/** Antwort kam nicht an, oder der Server ist gerade nicht bereit: später nochmals. */
class Netzfehler extends Error {}
/** Das Anmeldezeichen gilt nicht mehr. Die Daten bleiben, bis wieder angemeldet ist. */
class Abgelaufen extends Error {}

const SITZUNG = 'kgva.sitzung';
const FRIST_LESEN = 8000;
const FRIST_SCHREIBEN = 120000;
const SEITE = 500;

const lesen = (k, ersatz) => {
  try { const r = localStorage.getItem(k); return r ? JSON.parse(r) : ersatz; } catch { return ersatz; }
};
const schreiben = (k, v) => {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* voller Speicher */ }
};

/** 15 Zeichen a-z0-9: so will PocketBase seine Kennungen. */
export function neueId() {
  const zeichen = 'abcdefghijklmnopqrstuvwxyz0123456789';
  // 216 ist das grösste Vielfache von 36 unter 256 - darüber wäre die Verteilung schief.
  let id = '';
  while (id.length < 15) {
    for (const b of crypto.getRandomValues(new Uint8Array(20))) {
      if (b < 216 && id.length < 15) id += zeichen[b % 36];
    }
  }
  return id;
}
export const istPbId = (id) => /^[a-z0-9]{15}$/.test(String(id || ''));

/** Datensatz aus PocketBase -> Favorit, wie ihn die Oberfläche kennt. */
export function alsFavorit(r) {
  return {
    id: r.id,
    name: r.name,
    lat: r.lat,
    lng: r.lng,
    zoom: r.zoom || undefined,
    kind: r.kind || '',
    reise: r.reise || undefined,
    createdAt: r.angelegt || r.created
  };
}

/** Eine Tour ohne ihre Punkte - das, was die Liste braucht. */
export function alsTour(r, profil) {
  return {
    id: r.id,
    profil,
    name: r.name,
    art: r.art,
    begonnen: r.begonnen || null,
    beendet: r.beendet || null,
    stats: r.stats || undefined,
    createdAt: r.created
  };
}

export function createPocketbase({ basis, lokal }) {
  const url = (pfad) => `${basis}/api/${pfad}`;
  let sitzung = lesen(SITZUNG, null);
  const merke = (s) => {
    sitzung = s;
    if (s) schreiben(SITZUNG, s);
    else { try { localStorage.removeItem(SITZUNG); } catch { /* egal */ } }
  };

  async function abruf(pfad, { method = 'GET', body, frist = FRIST_LESEN } = {}) {
    const c = new AbortController();
    const uhr = setTimeout(() => c.abort(), frist);
    let r;
    try {
      r = await fetch(url(pfad), {
        method,
        signal: c.signal,
        headers: {
          ...(body ? { 'Content-Type': 'application/json' } : {}),
          ...(sitzung?.token ? { Authorization: sitzung.token } : {})
        },
        body: body ? JSON.stringify(body) : undefined
      });
    } catch {
      throw new Netzfehler('Server nicht erreichbar. Besteht eine Verbindung?');
    } finally {
      clearTimeout(uhr);
    }
    if (r.status === 401) throw new Abgelaufen('Bitte neu anmelden.');
    if (r.status === 429) throw new Netzfehler('Zu viele Versuche. Bitte in ein paar Minuten nochmals.');
    if (r.status >= 500) throw new Netzfehler(`Server antwortet nicht (${r.status}).`);
    if (r.status === 204) return null;
    const daten = await r.json().catch(() => null);
    if (!r.ok) {
      const e = new Error(daten?.message || `Fehler ${r.status}`);
      e.status = r.status;
      e.daten = daten?.data;
      throw e;
    }
    return daten;
  }

  const spaeterNochmals = (e) => e instanceof Netzfehler || e instanceof Abgelaufen;

  /** Alle Seiten einer Sammlung. Die Regeln liefern ohnehin nur die eigenen Zeilen. */
  async function alle(sammlung, zusatz = '') {
    const liste = [];
    for (let seite = 1; ; seite += 1) {
      const d = await abruf(`collections/${sammlung}/records?perPage=${SEITE}&page=${seite}&skipTotal=1${zusatz}`);
      liste.push(...(d?.items || []));
      if ((d?.items || []).length < SEITE) return liste;
    }
  }

  /* ---------------------------------------------- Vorgemerktes nachholen */

  const merkzettelName = (uid) => `kgva.ausstehend.${uid}`;
  const cacheName = (uid) => `kgva.pb.favoriten.${uid}`;
  const prefsName = (uid) => `kgva.pb.einstellungen.${uid}`;
  /** Touren liegen in denselben Ablagen wie die lokalen, unter eigenem Profil. */
  const tourProfil = (uid) => ({ id: `pb:${uid}` });

  async function senden(a) {
    const pfad = `collections/${a.sammlung}/records`;
    try {
      if (a.op === 'anlegen') await abruf(pfad, { method: 'POST', body: { id: a.id, ...a.daten }, frist: FRIST_SCHREIBEN });
      else if (a.op === 'aendern') await abruf(`${pfad}/${a.id}`, { method: 'PATCH', body: a.daten, frist: FRIST_SCHREIBEN });
      else await abruf(`${pfad}/${a.id}`, { method: 'DELETE' });
    } catch (e) {
      // Der erste Versuch kam an, nur die Antwort nicht: das Ziel ist schon erreicht.
      if (a.op === 'anlegen' && e.daten?.id?.code === 'validation_not_unique') return;
      if (a.op === 'loeschen' && e.status === 404) return;
      throw e;
    }
  }

  /** Einstellungen sind genau eine Zeile je Konto, mit dessen Kennung. */
  async function einstellungenSenden(uid, daten) {
    try {
      await abruf(`collections/einstellungen/records/${uid}`, { method: 'PATCH', body: daten });
    } catch (e) {
      if (e.status !== 404) throw e;
      await abruf('collections/einstellungen/records', { method: 'POST', body: { id: uid, benutzer: uid, ...daten } });
    }
  }

  /** Ausführen oder, wenn das Netz fehlt, vormerken. Die Reihenfolge bleibt erhalten. */
  async function auftrag(uid, a) {
    const zettel = lesen(merkzettelName(uid), []);
    if (!zettel.length) {
      try {
        await senden(a);
        return;
      } catch (e) {
        if (!spaeterNochmals(e)) throw e;
      }
    }
    zettel.push(a);
    schreiben(merkzettelName(uid), zettel);
  }

  let holtNach = null;
  /** Gibt zurück, ob jetzt alles beim Server ist. Läuft nie zweimal gleichzeitig. */
  function nachholen(uid) {
    if (holtNach) return holtNach;
    holtNach = (async () => {
      // Zuerst die Touren: ein vorgemerktes Umbenennen setzt voraus, dass es sie gibt.
      const touren = await lokal.tracks.list(tourProfil(uid));
      for (const t of touren.filter((x) => x.ausstehend)) {
        const voll = await lokal.tracks.get(tourProfil(uid), t.id);
        try {
          await tourHochladen(uid, voll);
          await lokal.tracks.update(tourProfil(uid), t.id, { ausstehend: false });
        } catch (e) {
          if (spaeterNochmals(e)) throw e;
          // Abgelehnt: sie bleibt im Gerät und sperrt nicht alles Übrige.
          console.warn('Tour abgelehnt:', t.id, e.message);
        }
      }
      let zettel = lesen(merkzettelName(uid), []);
      while (zettel.length) {
        try {
          await senden(zettel[0]);
        } catch (e) {
          // Was der Server endgültig ablehnt, käme auch morgen nicht durch. Es darf
          // nicht alles dahinter blockieren.
          if (spaeterNochmals(e)) throw e;
          console.warn('Änderung abgelehnt:', zettel[0], e.message);
        }
        zettel = zettel.slice(1);
        schreiben(merkzettelName(uid), zettel);
      }
      const p = lesen(`${prefsName(uid)}.offen`, null);
      if (p) {
        await einstellungenSenden(uid, p);
        try { localStorage.removeItem(`${prefsName(uid)}.offen`); } catch { /* egal */ }
      }
      return true;
    })().catch((e) => {
      if (spaeterNochmals(e)) return false;
      throw e;
    }).finally(() => { holtNach = null; });
    return holtNach;
  }

  async function tourHochladen(uid, t) {
    const { id, name, art, begonnen, beendet, stats, punkte = [] } = t;
    await senden({
      sammlung: 'touren',
      op: 'anlegen',
      id,
      daten: { benutzer: uid, name, art, begonnen, beendet, stats, punkte }
    });
  }

  /* ------------------------------------------------- Anmelden */

  const alsProfil = (record) => ({
    id: record.id,
    name: record.name || record.username,
    kennung: record.username,
    server: true,
    local: false
  });

  const auth = {
    /**
     * Beim Start. Das Zeichen wird erneuert, damit es nicht abläuft, solange die
     * Karte benutzt wird. Ohne Netz bleibt man angemeldet: gesperrt wird nur, wenn
     * der Server das Zeichen wirklich zurückweist.
     */
    async getSession() {
      if (!sitzung?.token) return null;
      try {
        const d = await abruf('collections/users/auth-refresh', { method: 'POST' });
        merke({ token: d.token, profil: alsProfil(d.record) });
      } catch (e) {
        if (e instanceof Abgelaufen || e.status === 403 || e.status === 404) {
          merke(null);
          return null;
        }
      }
      return sitzung?.profil || null;
    },

    async login(kennung, passwort) {
      merke(null);
      let d;
      try {
        d = await abruf('collections/users/auth-with-password', {
          method: 'POST',
          body: { identity: String(kennung || '').trim().toLowerCase(), password: passwort }
        });
      } catch (e) {
        throw new Error(e instanceof Netzfehler ? e.message : 'Name oder Passwort stimmt nicht.');
      }
      merke({ token: d.token, profil: alsProfil(d.record) });
      return sitzung.profil;
    },

    /** Vorgemerktes bleibt im Gerät und geht beim nächsten Anmelden hinaus. */
    async logout() {
      merke(null);
    },

    async passwortAendern(profil, alt, neu) {
      try {
        await abruf(`collections/users/records/${profil.id}`, {
          method: 'PATCH',
          body: { oldPassword: alt, password: neu, passwordConfirm: neu }
        });
      } catch (e) {
        if (e instanceof Netzfehler) throw new Error(e.message);
        if (e.daten?.oldPassword) throw new Error('Das aktuelle Passwort stimmt nicht.');
        if (e.daten?.password) throw new Error('Das neue Passwort braucht mindestens acht Zeichen.');
        throw e;
      }
      // Ein neues Passwort macht das alte Zeichen ungültig.
      return auth.login(profil.kennung, neu);
    },

    /** Der Anzeigename. Angemeldet wird weiter mit dem Anmeldenamen. */
    async nameAendern(profil, name) {
      try {
        const r = await abruf(`collections/users/records/${profil.id}`, { method: 'PATCH', body: { name } });
        merke({ token: sitzung.token, profil: alsProfil(r) });
        return sitzung.profil;
      } catch (e) {
        throw new Error(e instanceof Netzfehler ? e.message : 'Der Name liess sich nicht ändern.');
      }
    }
  };

  /* ------------------------------------------------- Favoriten */

  const favorites = {
    async list(p) {
      try {
        await nachholen(p.id);
        if (lesen(merkzettelName(p.id), []).length) return lesen(cacheName(p.id), []);
        const liste = (await alle('favoriten', '&sort=-created')).map(alsFavorit);
        schreiben(cacheName(p.id), liste);
        return liste;
      } catch (e) {
        if (spaeterNochmals(e)) return lesen(cacheName(p.id), []);
        throw e;
      }
    },
    async add(p, fav) {
      const eintrag = {
        ...fav,
        // Rückgängig bringt dieselbe Kennung zurück.
        id: istPbId(fav.id) ? fav.id : neueId(),
        createdAt: fav.createdAt || new Date().toISOString()
      };
      await auftrag(p.id, {
        sammlung: 'favoriten',
        op: 'anlegen',
        id: eintrag.id,
        daten: {
          benutzer: p.id,
          name: eintrag.name,
          lat: eintrag.lat,
          lng: eintrag.lng,
          zoom: eintrag.zoom,
          kind: eintrag.kind,
          reise: eintrag.reise,
          angelegt: eintrag.createdAt
        }
      });
      schreiben(cacheName(p.id), [eintrag, ...lesen(cacheName(p.id), [])]);
      return eintrag;
    },
    async update(p, id, patch) {
      await auftrag(p.id, { sammlung: 'favoriten', op: 'aendern', id, daten: patch });
      const liste = lesen(cacheName(p.id), []).map((f) => (f.id === id ? { ...f, ...patch } : f));
      schreiben(cacheName(p.id), liste);
      return liste.find((f) => f.id === id);
    },
    async remove(p, id) {
      await auftrag(p.id, { sammlung: 'favoriten', op: 'loeschen', id });
      schreiben(cacheName(p.id), lesen(cacheName(p.id), []).filter((f) => f.id !== id));
    }
  };

  /* ------------------------------------------------- Einstellungen */

  const prefs = {
    async load(p) {
      try {
        const d = await abruf(`collections/einstellungen/records/${p.id}`);
        const stand = { filters: d.filters };
        schreiben(prefsName(p.id), stand);
        return stand;
      } catch (e) {
        if (e.status === 404) return null;
        if (spaeterNochmals(e)) return lesen(prefsName(p.id), null);
        throw e;
      }
    },
    async save(p, daten) {
      schreiben(prefsName(p.id), daten);
      try {
        await einstellungenSenden(p.id, daten);
      } catch (e) {
        if (!spaeterNochmals(e)) throw e;
        // Nur der letzte Stand zählt - kein Merkzettel voller Häkchen.
        schreiben(`${prefsName(p.id)}.offen`, daten);
      }
    }
  };

  /* ------------------------------------------------- Touren */

  const FELDER = '&fields=id,name,art,begonnen,beendet,stats,created';

  const tracks = {
    async list(p) {
      const profil = tourProfil(p.id);
      try {
        await nachholen(p.id);
        const server = (await alle('touren', `&sort=-begonnen${FELDER}`)).map((r) => alsTour(r, profil.id));
        const hier = await lokal.tracks.list(profil);
        const bekannt = new Set(server.map((t) => t.id));
        // Beim Server Gelöschtes (auf dem anderen Gerät) verschwindet auch hier -
        // ausser es wartet noch aufs Hochladen.
        for (const t of hier) {
          if (!bekannt.has(t.id) && !t.ausstehend) await lokal.tracks.remove(profil, t.id);
        }
        for (const t of server) {
          const alt = hier.find((x) => x.id === t.id);
          if (alt) await lokal.tracks.update(profil, t.id, t);
          else await lokal.tracks.nurMerkmale(profil, t);
        }
      } catch (e) {
        if (!spaeterNochmals(e)) throw e;
      }
      return lokal.tracks.list(profil);
    },
    async get(p, id) {
      const profil = tourProfil(p.id);
      const hier = await lokal.tracks.get(profil, id);
      if (hier?.punkte?.length) return hier;
      const r = await abruf(`collections/touren/records/${id}`, { frist: FRIST_SCHREIBEN });
      const voll = { ...alsTour(r, profil.id), punkte: r.punkte || [] };
      // Einmal geholt, steht sie auch ohne Netz zur Verfügung.
      await lokal.tracks.add(profil, voll);
      return voll;
    },
    async add(p, tour) {
      const profil = tourProfil(p.id);
      const id = istPbId(tour.id) ? tour.id : neueId();
      const eintrag = await lokal.tracks.add(profil, { ...tour, id, ausstehend: true });
      try {
        await tourHochladen(p.id, { ...tour, id });
        await lokal.tracks.update(profil, id, { ausstehend: false });
        return { ...eintrag, ausstehend: false };
      } catch (e) {
        // Lehnt der Server ab, bleibt die Tour erhalten - nur eben im Gerät. Sie
        // zu verlieren wäre schlimmer als eine Meldung zu viel.
        if (!spaeterNochmals(e)) console.warn('Tour abgelehnt:', e.message);
        return eintrag;
      }
    },
    async update(p, id, patch) {
      const profil = tourProfil(p.id);
      const neu = await lokal.tracks.update(profil, id, patch);
      if (!neu?.ausstehend) await auftrag(p.id, { sammlung: 'touren', op: 'aendern', id, daten: patch });
      return neu;
    },
    async remove(p, id) {
      const profil = tourProfil(p.id);
      const t = (await lokal.tracks.list(profil)).find((x) => x.id === id);
      await lokal.tracks.remove(profil, id);
      if (!t?.ausstehend) await auftrag(p.id, { sammlung: 'touren', op: 'loeschen', id });
    }
  };

  /* ------------------------------------------------- Bestehendes übernehmen */

  /**
   * Was bisher im Browser lag, einmalig ins Konto. Jedes Stück wird erst entfernt,
   * wenn es angekommen ist: scheitert es unterwegs, geht es beim nächsten Mal weiter.
   */
  async function uebernehmen(p) {
    const zaehler = { favoriten: 0, touren: 0 };
    for (const [schluessel, liste] of lokal.alteFavoriten()) {
      const rest = [...liste];
      while (rest.length) {
        const { id: _alt, ...f } = rest[0];
        try {
          await favorites.add(p, f);
        } catch { break; }
        rest.shift();
        zaehler.favoriten += 1;
        lokal.alteFavoritenSetzen(schluessel, rest);
      }
    }
    for (const t of await lokal.alteTouren()) {
      const voll = await lokal.tracks.get({ id: t.profil }, t.id);
      const { id: _alt, profil: _p, ...rest } = voll || t;
      await tracks.add(p, rest);
      await lokal.tracks.remove({ id: t.profil }, t.id);
      zaehler.touren += 1;
    }
    const altePrefs = lokal.altePrefs();
    if (altePrefs && !(await prefs.load(p))) await prefs.save(p, altePrefs);
    lokal.altePrefsWeg();
    return zaehler;
  }

  return { auth, favorites, prefs, tracks, uebernehmen, nachholen };
}
